import React from "react";

const InnerSaboteursInMeetingsVideo = ({ subsection, saveProgress }) => {
  const about = (
    <div className="space-y-5 pt-2 pb-6 text-body leading-7 text-[#484848]">
      <p>
        This lesson explains why capable people still fade in meetings. It introduces saboteurs that hijack presence, like the judge, people pleaser, victim, avoider, hyper rational, and controller. It reframes every meeting as an audition where promotion perception is built long before appraisal. The shift is to spot the pattern early, change how you show up, and treat meeting moments as rehearsal for the next role.
      </p>
    </div>
  );

const transcript = (
  <div className="pt-2 pb-6 space-y-5 text-body leading-7 text-[#323232] overflow-y-scroll font-gotham">
    <p>
      We’ve already discussed that every meeting is a stage. Some deliver their
      lines like professionals, confident, composed, clear. Others fade into
      the background, trapped by something unseen.
    </p>

    <p>
      This unseen ghost is their saboteur. Let’s look at a few common ones.
    </p>

    <p>
      <strong>The Judge</strong>, the imposter voice.
      <br />
      Says, “I’m not good enough.” In meetings, it goes silent or overly
      critical. Meaning no visibility.
    </p>

    <p>
      <strong>The People Pleaser</strong>, always saying yes to please others.
      Overworked and invisible.
    </p>

    <p>
      <strong>The Victim</strong>, forever unlucky. They tell stories of how
      things went wrong, or others have wronged them, but never how they’ll
      make it right. They gain sympathy, not responsibility as they are seen
      as low agency.
    </p>

    <p>
      <strong>The Avoider</strong>, allergic to conflict. They keep peace in the
      room… but at the cost of progress. When decisions need force, they step
      back and the room forgets them.
    </p>

    <p>
      <strong>The Hyper-Rational</strong>, facts over feelings. They ask for
      data, logic, and proof. Impressive, yes, but leadership isn’t only
      analysis. It’s empathy.
    </p>

    <p>
      <strong>The Controller interrupts</strong>, directs, and dominates. They
      are seen as competent, but not collaborative.
    </p>

    <p>
      Promotable people recognize their saboteur before it steals the scene.
      If you haven’t met yours yet, maybe it’s time for an honest evaluation.
    </p>

    <p>
      One truth most people miss, your promotion isn’t decided in the
      appraisal meeting. It’s decided{" "}
      <strong>in the meetings that lead up to it.</strong>
    </p>

    <p>
      Every update, every status call, every moment you speak in front of
      stakeholders, is your audition, your chance to make the role.
    </p>

    <p>
      Stop thinking of meetings as interruptions to your work. They’re mirrors
      of your leadership.
    </p>

    <p>
      So, this week, look at your calendar again. Those blocks of time are not
      obstacles. They’re your opportunity to shine.
    </p>

    <p>
      And if there are none, it's time to carve some out
    </p>

    <p>
      When the spotlight turns to you, will you treat it like routine… or like
      rehearsal for your next role?
    </p>
  </div>
);

  // const relatedLinks = (
  //   <div className="pt-2 pb-6">
  //     <ul className="list-disc list-inside text-body space-y-2">
  //       <li>
  //         <button type="button" className="text-link">
  //           Download Slides
  //         </button>
  //       </li>
  //       <li>
  //         <button type="button" className="text-link">
  //           Reference Reading: Career Transitions Report 2025
  //         </button>
  //       </li>
  //     </ul>
  //   </div>
  // );

  const notes = (
    <div className="pt-2 pb-6">
      <textarea
        rows={4}
        placeholder="Write your notes here..."
        className="w-full border border-gray-200 rounded-xl p-3 text-body focus:ring-2 focus:ring-brand/40 focus:outline-none"
        onBlur={(e) => saveProgress("in_progress", { notes: e.target.value })}
      />
    </div>
  );

  return { about, transcript, notes };
};

export default InnerSaboteursInMeetingsVideo;
